import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { templates, type TemplateRevision } from '../lib/api';

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

interface TemplateRevisionSidebarProps {
  templateId: string;
  currentVersion: string;
  currentUpdatedAt: string;
  /** null = current version selected, string = revision timestamp selected */
  activeTimestamp: string | null;
  onSelectRevision: (revision: TemplateRevision) => void;
  onRestoreCurrent: () => void;
  onClose: () => void;
}

export function TemplateRevisionSidebar({
  templateId,
  currentVersion,
  currentUpdatedAt,
  activeTimestamp,
  onSelectRevision,
  onRestoreCurrent,
  onClose,
}: TemplateRevisionSidebarProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['template-revisions', templateId],
    queryFn: () => templates.revisions(templateId),
  });

  const revisions = data?.data || [];
  const [loadingTimestamp, setLoadingTimestamp] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSelect = async (rev: TemplateRevision) => {
    setLoadingTimestamp(rev.createdAt);
    setError(null);
    try {
      const result = await templates.revision(templateId, rev.createdAt);
      if (result.data) {
        onSelectRevision(result.data);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load revision');
    } finally {
      setLoadingTimestamp(null);
    }
  };

  const isCurrent = activeTimestamp === null;

  return (
    <div className="flex h-full w-72 flex-col border-l border-gray-800 bg-surface-900">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-800 px-4 py-3">
        <h3 className="text-sm font-semibold text-gray-200">Revision History</h3>
        <button
          onClick={onClose}
          className="rounded p-1 text-gray-500 hover:bg-gray-800 hover:text-gray-300"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Revision list (current + history) */}
      <div className="flex-1 overflow-y-auto">
        {error && (
          <div className="mx-4 mt-2 rounded bg-red-500/10 px-3 py-2 text-[11px] text-red-400">
            {error}
          </div>
        )}

        <ul className="divide-y divide-gray-800/50">
          {/* Current saved version */}
          <li>
            <button
              onClick={onRestoreCurrent}
              className={`w-full px-4 py-3 text-left transition-colors ${isCurrent ? 'bg-primary-500/5 border-l-2 border-l-primary-500' : 'hover:bg-gray-800/50'}`}
            >
              <div className="flex items-center gap-2">
                <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${isCurrent ? 'bg-primary-500/20 text-primary-400' : 'bg-gray-800 text-gray-400'}`}>
                  v{currentVersion}
                </span>
                <span className="rounded bg-green-500/10 px-1.5 py-0.5 text-[10px] font-medium text-green-400">
                  Current
                </span>
                <span className="text-[11px] text-gray-500">
                  {timeAgo(currentUpdatedAt)}
                </span>
              </div>
              <div className="mt-1 text-[11px] text-gray-600">
                {new Date(currentUpdatedAt).toLocaleString()}
              </div>
            </button>
          </li>

          {/* Past revisions */}
          {isLoading ? (
            <li className="flex items-center justify-center py-8">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
            </li>
          ) : revisions.length === 0 ? (
            <li className="px-4 py-6 text-center text-xs text-gray-500">
              No previous revisions yet.
            </li>
          ) : (
            revisions.map((rev) => {
              const isActive = activeTimestamp === rev.createdAt;
              const isRevLoading = loadingTimestamp === rev.createdAt;
              return (
                <li key={rev.createdAt}>
                  <button
                    onClick={() => handleSelect(rev)}
                    disabled={isRevLoading}
                    className={`w-full px-4 py-3 text-left transition-colors disabled:opacity-50 ${isActive ? 'bg-primary-500/5 border-l-2 border-l-primary-500' : 'hover:bg-gray-800/50'}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${isActive ? 'bg-primary-500/20 text-primary-400' : 'bg-gray-800 text-gray-400'}`}>
                        v{rev.version}
                      </span>
                      <span className="text-[11px] text-gray-500">
                        {isRevLoading ? 'Loading...' : timeAgo(rev.createdAt)}
                      </span>
                    </div>
                    <div className="mt-1 text-[11px] text-gray-600">
                      {new Date(rev.createdAt).toLocaleString()}
                    </div>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </div>
  );
}
